import find from 'lodash/find';
import last from 'lodash/last';
import uniqBy from 'lodash/uniqBy';

import { store } from './../store';
import { getFromLocalStorage, setToLocalStorage } from './../utils/utilsGlobal';
import {
  setCurrentRoom,
  setCurrentRoomFilter,
  setCurrentRoomSearchTerm,
  setCurrentRoomsByFilter,
  setCurrentRoomsBySearchTerm,
} from './../actions/actions';

import { logError } from './services';
import BaseSubscription from './BaseSubscription';
import {
  gql,
  sendAPIRequest,
  simplifyGraphQLJSON,
  insertGraphQLFragments,
} from './GraphQLClient';

import {
  fragmentRoomBasic,
  fragmentRoomFull,
  serializeRoomBasic,
  serializeRoomFull,
} from './serializers';


export default class RoomListSubscription extends BaseSubscription {

  DEFAULT_ROOM_ID = 'default';
  CHUNK_SIZE = 50;

  currentOperator = {};
  currentCompany = {};

  defaultFilter = null;
  filter = null;
  searchTerm = '';
  currentRoom = {};

  reachedEndOfRoomsByFilter = false;
  reachedEndOfRoomsBySearchTerm = false;

  allRoomsCache = [];
  roomsByFilter = [];
  roomsBySearchTerm = [];

  singleRoomQuery = insertGraphQLFragments(gql`
    query ($roomId: ID!) {
      room(roomId: $roomId) {
        ...fragmentRoomFull
      }
    }
  `, { fragmentRoomFull });

  roomsBySearchTermQuery = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $searchTerm: String!, $limit: Int!, $afterCursor: String) {
      searchRooms(companyId: $companyId, query: $searchTerm, first: $limit, after: $afterCursor) {
        edges {
          cursor
          node {
            ...fragmentRoomBasic
          }
        }
      }
    }
  `, { fragmentRoomBasic });

  roomsByFilterQuery = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $operatorId: ID, $limit: Int!, $afterCursor: String) {
      rooms(companyId: $companyId, employeeId: $operatorId, first: $limit, after: $afterCursor) {
        edges {
          cursor
          node {
            ...fragmentRoomBasic
          }
        }
      }
    }
  `, { fragmentRoomBasic });

  newRoomsQuery = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $limit: Int!, $afterCursor: String) {
      newRooms(companyId: $companyId, first: $limit, after: $afterCursor) {
        edges {
          cursor
          node {
            ...fragmentRoomBasic
          }
        }
      }
    }
  `, { fragmentRoomBasic });

  roomsQueue = insertGraphQLFragments(gql`
    query queueStatus($companyId: Uuid!) {
      queueStatus(companyId: $companyId) {
        unresponsiveRooms
        waitAssignRooms
        reservedRooms
        inProgressRooms
        availableEmployees
        workingEmployees
        totalEmployees
      }
    }
  `);

  initialize = async ({ currentOperator, currentCompany }) => {
    this.currentOperator = currentOperator;
    this.currentCompany = currentCompany;
    this.defaultFilter = await this.getDefaultFilter();
  };

  syncRoomListOnNewMessage = (message) => {
    const roomId = message.room.id;
    this.updateRoomById(roomId, message.room);

    const shouldAddRoomToRoomsByFilter = this.filter === message.room.assignedOperator.id || this.filter === 'all-rooms';
    if (shouldAddRoomToRoomsByFilter) {
      this.roomsByFilter = this.sortRoomsByFilter(
        uniqBy([ ...this.roomsByFilter, message.room ], 'id')
      );
    }
    else {
      this.roomsByFilter = this.sortRoomsByFilter(
        this.roomsByFilter.filter(room => room.id !== roomId)
      );
    }
    this.saveToAllRoomsCache([message.room]);
    this.setReduxData(setCurrentRoomsByFilter, this.roomsByFilter);
  };

  sortRoomsByFilter = (rooms) => {
    return rooms.sort((a,b) => {
      const aIsCaseOpen = a.isCaseOpen;
      const bIsCaseOpen = b.isCaseOpen;
      const aHasMessages = new Date(a.lastMessageAt);
      const bHasMessages = new Date(b.lastMessageAt);

      if (aIsCaseOpen && !bIsCaseOpen) {
        return -1;
      }
      else if (!aIsCaseOpen && bIsCaseOpen) {
        return 1;
      }
      else if (aHasMessages && !bHasMessages) {
        return -1;
      }
      else if (!aHasMessages && bHasMessages) {
        return 1;
      }
      else {
        const aTimestamp = new Date(a.lastMessageAt || a.insertedAt);
        const bTimestamp = new Date(b.lastMessageAt || b.insertedAt);
        return aTimestamp > bTimestamp ? -1 : 1;
      }
    });
  };

  getLatestUpdatedRoom = () => {
    return this.roomsByFilter.slice().sort((a,b) => {
      const aTimestamp = new Date(a.lastMessageAt || a.insertedAt);
      const bTimestamp = new Date(b.lastMessageAt || b.insertedAt);
      return aTimestamp > bTimestamp ? -1 : 1;
    })[0];
  };

  saveToAllRoomsCache = (newRooms) => {
    const sanitizedNewRooms = newRooms.filter(room => room.id !== this.DEFAULT_ROOM_ID);
    this.allRoomsCache = uniqBy([ ...sanitizedNewRooms, ...this.allRoomsCache ], 'id');
  };

  updateRoomById = (roomId, diff) => {
    const mapRoom = room => {
      if (room.id === roomId) {
        return { ...room, ...diff };
      }
      else {
        return room;
      }
    };
    this.allRoomsCache = this.allRoomsCache.map(mapRoom);
    this.roomsByFilter = this.sortRoomsByFilter( this.roomsByFilter.map(mapRoom) );
    this.roomsBySearchTerm = this.roomsBySearchTerm.map(mapRoom);
    this.setReduxData(setCurrentRoomsByFilter, this.roomsByFilter);
    this.setReduxData(setCurrentRoomsBySearchTerm, this.roomsBySearchTerm);

    if (roomId === this.currentRoom.id) {
      this.onCurrentRoomChange({ ...this.currentRoom, ...diff });
    }
  };

  setReduxData = (actionFunction, data) => {
    store.dispatch(actionFunction(data));
  };

  onCurrentRoomChange = (newRoom) => {
    if (newRoom?.id) {
      this.currentRoom = newRoom;
      this.setReduxData(setCurrentRoom, newRoom);
      return newRoom;
    }
  };

  generateDefaultRoomData = () => {
    return serializeRoomBasic({ id: this.DEFAULT_ROOM_ID });
  };

  setCurrentRoomById = (roomId) => {
    const fallbackToFirstRoom = () => {
      const basicFirstRoom = this.roomsByFilter[0];
      if (basicFirstRoom?.id === this.DEFAULT_ROOM_ID) {
        this.onCurrentRoomChange(basicFirstRoom);
        return Promise.resolve(basicFirstRoom);
      }
      else {
        this.onCurrentRoomChange(basicFirstRoom);
        return this.fetchSingleRoom(basicFirstRoom.id).then(this.onCurrentRoomChange);
      }
    };

    if (roomId && roomId !== this.DEFAULT_ROOM_ID) {
      const basicCurrentRoom = find(this.allRoomsCache, { id: roomId });
      this.onCurrentRoomChange(basicCurrentRoom);
      return this.fetchSingleRoom(roomId)
        .then(this.onCurrentRoomChange)
        .catch(fallbackToFirstRoom);
    }
    else {
      return fallbackToFirstRoom();
    }
  };

  fetchSingleRoom = (roomId) => {
    return sendAPIRequest(this.singleRoomQuery, { roomId }).then(serializeRoomFull);
  };

  getDefaultFilter = async () => {
    let filter;
    const localStorageFilter = getFromLocalStorage('ROOM_FILTER');

    if (localStorageFilter) {
      try {
        // Try to use locally stored filter to see if it's valid (i.e. returns rooms without errors).
        // Otherwise fall back to "My rooms" (or "All rooms" if logged in as a manager)
        await this.fetchRooms({ filter: localStorageFilter, limit: 0 }).catch(e => {
          e.overrideErrorNotification({ alert: false });
          throw e;
        });
        filter = localStorageFilter;
      }
      catch (e) {}
    }

    if (!filter) {
      filter = this.currentOperator.isManager ? 'all-rooms' : this.currentOperator.id;
    }
    return filter;
  };

  setRoomsByFilter = (filter = '') => {
    this.filter = filter;
    this.reachedEndOfRoomsByFilter = false;

    setToLocalStorage('ROOM_FILTER', filter);
    this.setReduxData(setCurrentRoomFilter, filter);

    return this.fetchRooms({ filter }).then(rooms => {
      if (!rooms.length) {
        rooms = [ this.generateDefaultRoomData() ];
      }
      this.roomsByFilter = this.sortRoomsByFilter(rooms);
      this.saveToAllRoomsCache(this.roomsByFilter);
      this.setReduxData(setCurrentRoomsByFilter, this.roomsByFilter);
      return this.roomsByFilter;
    });
  };

  setRoomsBySearchTerm = (searchTerm = '') => {
    this.searchTerm = searchTerm;
    this.reachedEndOfRoomsBySearchTerm = false;
    this.setReduxData(setCurrentRoomSearchTerm, searchTerm);

    if (!searchTerm) {
      this.roomsBySearchTerm = [];
      this.reachedEndOfRoomsBySearchTerm = true;
      this.setReduxData(setCurrentRoomsBySearchTerm, []);
      return Promise.resolve([]);
    }

    return this.fetchRooms({ searchTerm }).then(rooms => {
      this.roomsBySearchTerm = rooms;
      this.saveToAllRoomsCache(rooms);
      this.setReduxData(setCurrentRoomsBySearchTerm, rooms);
      return rooms;
    });
  };

  searchRooms = (searchTerm) => {
    return this.fetchRooms({ searchTerm }).then(rooms => {
      this.roomsBySearchTerm = rooms;
      this.saveToAllRoomsCache(rooms);
      this.setReduxData(setCurrentRoomsBySearchTerm, rooms);
      return rooms;
    });
  };

  fetchNextChunk = (type) => {
    let fetchParams = {};
    if (type === 'filter') {
      fetchParams = {
        filter: this.filter,
        afterCursor: last(this.roomsByFilter).cursor,
      };
    }
    else if (type === 'searchTerm') {
      fetchParams = {
        searchTerm: this.searchTerm,
        afterCursor: last(this.roomsBySearchTerm).cursor,
      };
    }
    if (!fetchParams.afterCursor) {
      const errorMessage = 'RoomListSubscription: Failed to fetch next room chunk - no cursors found';
      logError(errorMessage);
      return Promise.reject({ message: errorMessage });
    }
    return this.fetchRooms(fetchParams).then(processedRooms => {
      if (type === 'filter') {
        this.roomsByFilter = this.sortRoomsByFilter(
          uniqBy([ ...this.roomsByFilter, ...processedRooms ], 'id')
        );
        this.setReduxData(setCurrentRoomsByFilter, this.roomsByFilter);
      }
      else if (type === 'searchTerm') {
        this.roomsBySearchTerm = uniqBy([ ...this.roomsBySearchTerm, ...processedRooms ], 'id');
        this.setReduxData(setCurrentRoomsBySearchTerm, this.roomsBySearchTerm);
      }
      this.saveToAllRoomsCache(processedRooms);
      return processedRooms;
    });
  };

  fetchRooms = (params) => {
    const {
      filter,
      searchTerm,
      limit = this.CHUNK_SIZE,
      afterCursor = null,
    } = params;

    let query;
    let variables = {
      companyId: this.currentCompany.id,
      afterCursor,
      limit,
    };

    if (filter === 'new-rooms') {
      query = this.newRoomsQuery;
    }
    else if (filter === 'all-rooms') {
      query = this.roomsByFilterQuery;
    }
    else if (filter) {
      query = this.roomsByFilterQuery;
      variables.operatorId = filter;
    }
    else if (typeof searchTerm === 'string') {
      query = this.roomsBySearchTermQuery;
      variables.searchTerm = searchTerm;
    }

    return sendAPIRequest(query, variables).then(rooms => {
      const processedRooms = simplifyGraphQLJSON(rooms).map(serializeRoomBasic);
      const reachedEndOfResults = processedRooms.length < limit;
      if (filter) {
        this.reachedEndOfRoomsByFilter = reachedEndOfResults;
      }
      else if (searchTerm) {
        this.reachedEndOfRoomsBySearchTerm = reachedEndOfResults;
      }
      return processedRooms;
    });
  };

  /**
   * Получение списка очередей
   *
   */
  getRoomQueue = (companyId) => {
    return sendAPIRequest(this.roomsQueue, {companyId}).then(queue => {
      return queue;
    });
  };

  reset = () => {
    this.filter = null;
    this.searchTerm = '';
    this.defaultFilter = null;
    this.allRoomsCache = [];
    this.roomsByFilter = [];
    this.roomsBySearchTerm = [];
    this.currentRoom = serializeRoomFull({});

    this.reachedEndOfRoomsByFilter = false;
    this.reachedEndOfRoomsBySearchTerm = false;

    this.setReduxData(setCurrentRoom, serializeRoomFull({}));
    this.setReduxData(setCurrentRoomFilter, null);
    this.setReduxData(setCurrentRoomSearchTerm, '');
    this.setReduxData(setCurrentRoomsByFilter, []);
    this.setReduxData(setCurrentRoomsBySearchTerm, []);
  };
}
